<template>
  <div class="container checkout">
    <div class="checkout__header">
      <a :href="clientModel.startPageUrl" class="checkout-header__logo">
        <oie-use-svg svg="olsson-logo" class="checkout-header__logo-svg"></oie-use-svg>
      </a>
    </div>

    <template v-if="order.displayErrorMessage">
      <div v-html="t('errorInformation')"></div>
    </template>

    <template v-if="!order.displayErrorMessage">
      <div class="checkout__page-content">
        <div class="checkout__page-header-container">
          <h1 class="checkout__page-header">
            {{ t('orderRecieptTitle') }}
          </h1>

          <button v-if="mq('small')" class="checkout__page-print-button" @click="printPage()">
            {{ t('orderRecieptPrintLabel') }}
            <oie-use-svg svg="print" class="checkout__page-print-button-icon"></oie-use-svg>
          </button>
        </div>

        <div class="checkout__page-content-action">
          <p class="checkout__page-describing-text" v-html="t('orderDetailsDescription')"></p>
          <div class="checkout-confirm-action__buttons">
            <oie-base-button
              is-type="a"
              :url="clientModel.startPageUrl"
              css-classes="button--with-border button--green checkout-confirm-action__button"
              :text="t('continueShopping')">
            </oie-base-button>
            <oie-base-button
              is-type="a"
              :url="clientModel.regretPurchasePageUrl"
              css-classes="button--with-border button--green checkout-confirm-action__button"
              :text="t('regretPurchase')">
            </oie-base-button>
          </div>
        </div>
        <!-- giftcard info -->
        <template v-if="order.orderHasGiftCard">
          <oie-message-box
            class="message-box--compact"
            color="orange"
            v-html="t('orderGiftCardInformation')">
          </oie-message-box>
        </template>
      </div>

      <oie-layout-grid v-if="!isOrderRequestVisitorState" css-classes="checkout__order-information-grid">
        <oie-island-soil :header="t('orderInformationHeaderLabel')">
          <oie-key-value-table>
            <oie-key-value-table-row :label="t('orderCustomerNumberLabel')" :value="order.orderHead.customerNo"></oie-key-value-table-row>
            <oie-key-value-table-row :label="t('orderNumberLabel')" :value="order.orderHead.orderNr"></oie-key-value-table-row>
            <oie-key-value-table-row :label="t('orderDateLabel')" :value="order.orderHead.displayOrderDate"></oie-key-value-table-row>
            <oie-key-value-table-row :label="t('orderDeliveryMethodLabel')" :value="order.orderHead.modeOfDelivery"></oie-key-value-table-row>
            <oie-key-value-table-row :label="t('orderPaymentLabel')" :value="order.orderHead.paymentTerms"></oie-key-value-table-row>
            <oie-key-value-table-row v-if="order.partDeliveryInformation" :label="tStatic('orderPartDeliveryLabel')" :value="order.partDeliveryInformation"></oie-key-value-table-row>
            <oie-key-value-table-row :label="t('orderCommentLabel')" :value="order.orderHead.comment"></oie-key-value-table-row>
          </oie-key-value-table>
        </oie-island-soil>
        <oie-island-soil :header="t('orderInvoiceAddressHeader')">
          <oie-key-value-table>
            <oie-key-value-table-row :value="order.orderHead.invoiceAddress.name"></oie-key-value-table-row>

            <oie-key-value-table-row v-if="order.orderHead.invoiceAddress.addressCo">
              {{ order.orderHead.invoiceAddress.addressCo }} <br>
            </oie-key-value-table-row>

            <oie-key-value-table-row>
              {{ order.orderHead.invoiceAddress.address1 }}

              <template v-if="order.orderHead.invoiceAddress.address2">
                <br> {{ order.orderHead.invoiceAddress.address2 }}
              </template>
            </oie-key-value-table-row>

            <oie-key-value-table-row>
              {{ order.orderHead.invoiceAddress.zipCode }} {{ order.orderHead.invoiceAddress.city }}
            </oie-key-value-table-row>

            <oie-key-value-table-row>
              {{ order.orderHead.invoiceAddress.country }}
            </oie-key-value-table-row>
          </oie-key-value-table>
        </oie-island-soil>

        <oie-island-soil :header="t('orderDeliveryAddressHeader')">
          <oie-key-value-table>
            <oie-key-value-table-row v-if="order.orderHead.deliveryAddress.name.trim() !== ''" :value="order.orderHead.deliveryAddress.name"></oie-key-value-table-row>

            <oie-key-value-table-row v-if="order.orderHead.deliveryAddress.addressCo">
              {{ order.orderHead.deliveryAddress.addressCo }} <br>
            </oie-key-value-table-row>

            <oie-key-value-table-row>
              {{ order.orderHead.deliveryAddress.address1 }}

              <template v-if="order.orderHead.deliveryAddress.address2">
                <br> {{ order.orderHead.deliveryAddress.address2 }}
              </template>
            </oie-key-value-table-row>

            <oie-key-value-table-row>
              {{ order.orderHead.deliveryAddress.zipCode }} {{ order.orderHead.deliveryAddress.city }}
            </oie-key-value-table-row>

            <oie-key-value-table-row>
              {{ order.orderHead.deliveryAddress.country }}
            </oie-key-value-table-row>
          </oie-key-value-table>
        </oie-island-soil>
      </oie-layout-grid>

      <oie-order-reciept-product-table :rows="order.orderRows" class="history-details-products__table product-rows--readonly"></oie-order-reciept-product-table>

      <section class="checkout-summary">
        <div class="checkout-summary__row">
          <div class="checkout-summary__row-description">
            {{ t('summaryPriceOrderLabel') }}
          </div>
          <div class="checkout-summary__row-price">
            {{ formatPrice(order.orderHead.orderSum) }}
          </div>
        </div>

        <div v-if="!isOrderRequestVisitorState" class="checkout-summary__row">
          <div class="checkout-summary__row-description">
            {{ t('summaryShippingOrderLabel') }}
          </div>
          <div class="checkout-summary__row-price">
            <span class="checkout-summary__row-price">{{ formatPrice(order.orderHead.shipmentCost) }}</span>
          </div>
        </div>

        <div v-if="order.orderHead.packagingCost" class="checkout-summary__row">
          <div class="checkout-summary__row-description">
            {{ t('summaryHandlingOrderLabel') }}
          </div>
          <div class="checkout-summary__row-price">
            <span class="checkout-summary__row-price">{{ formatPrice(order.orderHead.packagingCost) }}</span>
          </div>
        </div>

        <div v-if="order.orderHead.orderDiscount" class="checkout-summary__row">
          <div class="checkout-summary__row-description">
            {{ tStatic('orderDiscount') }}
          </div>
          <div class="checkout-summary__row-price">
            {{ formatPrice(-order.orderHead.orderDiscount) }}
          </div>
        </div>

        <div class="checkout-summary__row">
          <div class="checkout-summary__row-description">
            {{ t('summaryVATOrderLabel') }}
          </div>
          <div class="checkout-summary__row-price">
            {{ formatPrice(order.orderHead.vatSum) }}
          </div>
        </div>

        <div class="checkout-summary__row">
          <div class="checkout-summary__row-description">
            {{ t('summaryTotalOrderLabel') }}
          </div>
          <div class="checkout-summary__row-price">
            {{ formatPrice(order.totalSum) }}
          </div>
        </div>
      </section>
    </template>

    <oie-base-button
      is-type="a"
      :url="clientModel.startPageUrl"
      css-classes="button--with-border button--green checkout-confirm-action__button"
      :text="t('continueShopping')">
    </oie-base-button>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import nextStepMixin from '@/mixins/checkout-next-step.js'
import { mapGetters, mapActions } from 'vuex'
import gtm from '@/core/enhanced-ecommerce-gtm'

export default {
  mixins: [nextStepMixin],
  props: {
    orderNumber: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    order: {
      partDelivery: false,
      partDeliveryInformation: '',
      orderHead: {
        orderNr: '',
        orderDate: '',
        customerNo: '',
        currency: '',
        paymentTerms: '',
        modeOfDelivery: '',
        shipmentId: '',
        ourRef: '',
        invoiceRefernce: '',
        shipmentCost: 0,
        packagingCost: 0,
        vatSum: 0,
        orderSum: 0,
        comment: '',
        deliveryAddress: {
          name: '',
          address1: '',
          address2: '',
          zipCode: '',
          city: '',
          country: ''
        },
        invoiceAddress: {
          name: '',
          address1: '',
          address2: '',
          zipCode: '',
          city: '',
          country: ''
        },
        email: ''
      },
      orderHasGiftCard: false,
      orderRows: [],
      orderInformation: ''
    },
    selectedCardPayment: null,
    isAuthenticated: true // todo: remove me: this will toggle the address/user input state
  }),
  computed: {
    ...mapGetters(['enableGtm']),
    ...mapGetters('checkout', ['isOrderRequestVisitorState'])
  },
  mounted() {
    this.clearActiveSteps()

    if(this.orderNumber !== '') {
      this.clearCheckout()

      httpService.get(`order/commerce/${this.orderNumber}`, { orderNumber: this.orderNumber, id: this.id }).then((response) => {
        this.order = response.data

        if (this.enableGtm) {
          const purchaseAlreadyAdded = localStorage.getItem(this.orderNumber)
          if(!purchaseAlreadyAdded) {
            gtm.addPurchase(this.order)
            localStorage.setItem(this.orderNumber, true)
          }
        }
      })
    }

    // LEAVE: test data
    // dev: Zoran
    // this.order = { 'totalSum': 5285.0, 'orderHead': { 'orderNr': '6160064', 'orderDate': '2020-05-27T08:24:03.893Z', 'displayOrderDate': '2020-05-27', 'customerNo': '1', 'currency': 'SEK', 'paymentTerms': 'Direktbanksbetalning', 'modeOfDelivery': 'PostNord Parcel Norden', 'shipmentId': null, 'ourRef': '', 'invoiceReference': null, 'shipmentCost': 0.00, 'packagingCost': 0.0, 'vatSum': 1057.000000000, 'orderSum': 4228.00, 'comment': '', 'deliveryAddress': { 'name': '', 'address1': '(test) Gatan 4', 'address2': '', 'zipCode': '30249', 'city': 'Halmstad', 'country': 'Sverige', 'mobile': null }, 'invoiceAddress': { 'name': '(test) Zoran', 'address1': '(test) Gatan 4', 'address2': '', 'zipCode': '30249', 'city': 'Halmstad', 'country': 'Sverige', 'mobile': null } }, 'orderRows': [{ 'radNr': '', 'status': '', 'artNr': '500-52-R2000-S4', 'artName': 'Ingavel GSV50, med säkerhetsventil', 'imageUrl': '/globalassets/pim/50/500-52-R2000-S4_1.jpeg', 'quantity': 4, 'sendNowQuantity': 4, 'sendLaterQuantity': 0, 'deliveredQuantity': 0, 'remainingQuantity': 0, 'estimatedDeliveryDate': '0001-01-01T00:00:00', 'priceExcludedVat': 2196.000000000, 'priceForEachItem': 549.000000000, 'comment': '' }, { 'radNr': '', 'status': '', 'artNr': '47667-12', 'artName': 'Ventilsektion NG06 C01 12 V', 'imageUrl': '/globalassets/pim/47/47667_1.jpeg', 'quantity': 1, 'sendNowQuantity': 1, 'sendLaterQuantity': 0, 'deliveredQuantity': 0, 'remainingQuantity': 0, 'estimatedDeliveryDate': '0001-01-01T00:00:00', 'priceExcludedVat': 1016.000000000, 'priceForEachItem': 1016.000000000, 'comment': '' }, { 'radNr': '', 'status': '', 'artNr': '47667-24', 'artName': 'Ventilsektion NG06 C01, 24 V', 'imageUrl': '/globalassets/pim/47/47667_1.jpeg', 'quantity': 1, 'sendNowQuantity': 1, 'sendLaterQuantity': 0, 'deliveredQuantity': 0, 'remainingQuantity': 0, 'estimatedDeliveryDate': '0001-01-01T00:00:00', 'priceExcludedVat': 1016.000000000, 'priceForEachItem': 1016.000000000, 'comment': '' }], 'displayErrorMessage': false }
  },
  methods: {
    ...mapActions('checkout', ['clearCheckout']),
    printPage() {
      window.print()
    }
  }
}
</script>

<style lang="scss">
.checkout-header__logo-element {
  max-width: 100%;
}
.checkout__page-describing-text {
  max-width: 40rem;
  margin: 0 0 measure(half) 0;

  @include media(small) {
    margin: 0 measure(full) 0 0;
  }
}
.checkout-confirm-action__buttons {
  display: flex;
  flex-direction: column;
  gap: measure(quarter);
  white-space: nowrap;

  @include media(small) {
    flex-direction: row;
  }
}
.checkout__page-text-bold {
  @include body-semi-bold;

  @include media(small) {
    @include h3-semi-bold;
  }
}

.checkout-section__item--receipt {
  width: 100%;
}

.checkout-section__selected-shipping {
  position: relative;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    flex-shrink: 0;
    width: 1.875rem;
    height: 1.875rem;
    border: 2px solid color(green-2);
    border-radius: 50%;
    margin-right: measure(half);
    transition: all 100ms linear;
    background-color: color(green-2);
  }

  &:after {
    content: '' ;
    position: absolute;
    top: 50%;
    left: .4rem;
    transform: translateY(-50%);
    background-image: url("data:image/svg+xml,%3Csvg width='30' height='28' viewBox='0 0 30 28' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.034 27.39L0 16.842l3.3-3.424 7.27 6.95L26.279.8 30 3.764z' fill='%23FFF' fill-rule='evenodd'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
    width: 1.1rem;
    height: 1.1rem;
  }
}

.checkout__order-information-grid {
  grid-template-columns: 1fr;

  @include media(medium) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.checkout__page-content-action {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin: measure(full) 0;

  @include media(small) {
    flex-direction: row;
    margin-top: measure(full);
  }
}
</style>
